import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputBase,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AddToDriveIcon,
  ArrowBackIcon,
  CloseIcon,
  DeleteIcon,
  DescriptionIcon,
  DoneAllIcon,
  DriveFileMoveIcon,
  DriveFileRenameOutlineIcon,
  FolderIcon,
  FolderOpenIcon,
  ImageIcon,
  RemoveIcon,
  UploadFileIcon,
} from "../icons";
import { useRecoilState, useRecoilValue } from "recoil";
import { tempFileState, selectedFileState, ngDataState, serverFileSecondState, exceptFileState } from "../recoil";
import { IDataSetObject, IFileObject, IImageInfo } from "@types";
import { toast } from "react-toastify";
import { imageFlat } from "../utils/utils";
import ProjectFileModal from "./ProjectFileModal";
import { getProject } from "../services/projectService";
import { IRenderDSChipsProps } from "@types";
import { t } from "i18next";
import { uploadFile } from "../services/fileService";
import { useParams } from "react-router-dom";
import { getPathData, SearchIcon } from "common-lib";
import { createServerDir, getAllFilesFromDirs, searchPathRecursive, SearchMatch } from "../services/datasetService";
import { useTutorial } from "../context/TutorialContext";
import { TUTORIALS } from "../tutorial";

interface ServerModalProps {
  onClick?: () => void;
  selectedTab?: string;
  step?: string; // "검사"일 때만 except.txt(학습 제외 → valid) 첨부 UI 노출
  onRegisterDataset?: (selectedDataset: IDataSetObject[]) => void;
  onConfirm?: () => void; // 지정 시 OK 버튼이 기본 등록 대신 이 함수 실행 (프로젝트 설정 페이지용)
  contentSx?: object;
}

interface ProjectImageLabel {
  projectImage: { path: string };
}

interface NGDataItem {
  projectImageLabel: ProjectImageLabel[];
}

// 라벨별 Chip 색상. OK/NG/unlabeled 고정, 나머지는 해시 → 색상(hue) 분산
const FIXED_LABEL_HUES: Record<string, number | null> = { ok: 142, ng: 0, unlabeled: null };
const labelChipSx = (label: string) => {
  const key = String(label ?? "").toLowerCase();
  let hue: number | null;
  if (key in FIXED_LABEL_HUES) hue = FIXED_LABEL_HUES[key];
  else {
    let h = 0;
    for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
    hue = (Math.abs(h) * 137.508) % 360;
  }
  const sat = hue === null ? 0 : 70;
  hue = hue ?? 0;
  return {
    background: `hsla(${hue}, ${sat}%, 50%, 0.12)`,
    border: `1px solid hsla(${hue}, ${sat}%, 50%, 0.4)`,
    color: `hsl(${hue}, ${sat}%, ${sat ? 40 : 45}%)`,
  };
};

const modalContentStyle = {
  width: "min(1200px, 92vw)",
  height: "min(650px, 85vh)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const bottomBoxStyle = {
  // position: "sticky",
  // bottom: 0,
  // left: 0,
  width: "100%",
  height: "fit-content",
  gap: 1,
  p: 1,
  bgcolor: "background.paper",
  borderTop: "1px solid",
  borderColor: "divider",
  zIndex: 1,
};

export const RenderChips: React.FC<IRenderDSChipsProps> = ({ id, project_name, handleDelete }) => {
  if (project_name.length === 0) return null;

  return (
    <Tooltip title={project_name} arrow placement="top" sx={{ cursor: "pointer" }}>
      <Chip label={project_name} onDelete={handleDelete} />
    </Tooltip>
  );
};

export default function ServerFileModal({
  onClick,
  selectedTab,
  step,
  onRegisterDataset,
  onConfirm,
  contentSx,
}: ServerModalProps) {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [isOpenSelectLabel, setIsOpenSelectLabel] = useState<boolean>(false);

  const [dirs, setDirs] = useState<string[]>([]); // 현재 경로에서 불러온 폴더들
  const [files, setFiles] = useState<string[]>([]); // 현재 경로에서 불러온 파일들
  const [currentPath, setCurrentPath] = useState<string>("/data1/datasets/"); // 파일들 조회할 서버 경로
  const lastValidPathRef = useRef<string>("/data1/datasets/"); // 마지막으로 조회에 성공한 경로 (실패 시 복귀용)
  const tutorialBasePathRef = useRef<string>("/data1/datasets/"); // 튜토리얼에서 진입 깊이를 재는 기준 경로
  const [ctrlPressed, setCtrlPressed] = useState(false); // ctrl 키 press T/F
  const [selectedRows, setSelectedRows] = useState<{
    dirs: any[];
    files: any[];
  }>({ dirs: [], files: [] }); // (왼쪽) 추가를 위해 클릭된 폴더 및 파일

  const [selectedDataSet, setSelectedDataSet] = useState<IDataSetObject[]>([]); // [분석] 선택된 프로젝트 데이터 - 이름 변경 필요
  const [selectedServerRows, setSelectedServerRows] = useState<string[]>([]); // (오른쪽) tempFiles에 들어간 데이터들중 선택된 데이터
  const [serverFiles, setServerFiles] = useRecoilState(serverFileSecondState); // 최종적으로 선택되어 설정된 이미지 데이터들 (string[] : 경로값들)
  const [serverFilesNames, setServerFilesNames] = useState<string[]>([]);

  const [tempFiles, setTempFiles] = useRecoilState(tempFileState); // (오른쪽) 데이터셋으로 설정할 선택된 이미지들 - 확정 전
  const [selectedFile, setSelectedFile] = useRecoilState(selectedFileState);
  // tempFiles/selectedFile은 recoil이라 모달이 닫혀도 남는다. 열린 시점 값을 스냅샷 해두고
  // OK 없이 닫히면(취소 버튼 / ESC / 바깥 클릭) 이번에 추가한 선택은 버리고 되돌린다
  const snapshotRef = useRef({ files: tempFiles, projects: selectedFile });
  const confirmedRef = useRef(false);
  const [visibleCountRight, setVisibleCountRight] = useState<number>(100);
  const [totalFiles, setTotalFiles] = useState<IFileObject[]>([]);
  const [selectedRowId, setSelectedRowId] = useState<string[]>([]); // 선택된 행의 ID 배열
  const ngData = useRecoilValue(ngDataState);
  const [selectedLabel, setSelectedLabel] = useState("unlabeled");

  const [exceptPaths, setExceptPaths] = useRecoilState(exceptFileState); // except.txt 경로들 (학습 제외 → valid)
  const exceptInputRef = useRef<HTMLInputElement>(null);
  const [isOpenExceptPaste, setIsOpenExceptPaste] = useState(false); // 파일 반출이 막힌 PC용: 내용 붙여넣기
  const [exceptText, setExceptText] = useState("");
  const exceptSet = useMemo(() => new Set(exceptPaths), [exceptPaths]);

  const [searchQuery, setSearchQuery] = useState("");
  const [deepMatches, setDeepMatches] = useState<SearchMatch[]>([]); // 하위 폴더까지 재귀 탐색한 검색 결과
  const [deepSearch, setDeepSearch] = useState({ running: false, scanned: 0, truncated: false });
  // 검색 중 전체선택은 "화면에 그린 상한(500건)"이 아니라 "검색 결과 전체"를 뜻한다.
  // 실제 파일 펼치기는 백엔드가 하므로 수천~수만건도 state에 담지 않는다
  const [selectAllMatches, setSelectAllMatches] = useState(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const addAbortRef = useRef<AbortController | null>(null); // 파일 탐색 중지용
  const { run, nextSubStep, tutorialKey, stepIndex } = useTutorial();

  const [visibleCountLeft, setVisibleCountLeft] = useState<number>(100);
  const lastTempClickIndexRef = useRef<number | null>(null); // (오른쪽) shift 범위선택 기준 인덱스
  const lastRowClickIdRef = useRef<string | null>(null); // (왼쪽) shift 범위선택 기준 행 id

  const loadMoreData = useCallback(() => {
    setVisibleCountRight((prevCount) => prevCount + 100); // 보이는 데이터 수 증가
  }, []);

  // 스크롤 이벤트 핸들러 - 바닥 근처에서 loadMore 호출
  const handleScroll = (loadMore: () => void) => (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      loadMore();
    }
  };

  // 경로/검색어 바뀌면 왼쪽 목록 표시 개수 초기화
  useEffect(() => {
    setVisibleCountLeft(100);
  }, [currentPath, searchQuery]);

  // 검색어 입력 시 현재 경로 아래를 재귀 탐색
  useEffect(() => {
    const query = searchQuery.trim();
    setSelectAllMatches(false);
    setDeepMatches([]);
    setDeepSearch({ running: false, scanned: 0, truncated: false });
    if (!query) return;

    const controller = new AbortController();
    // 타이핑 중 매 글자마다 탐색이 돌지 않도록 디바운스
    const timer = setTimeout(() => {
      setDeepSearch({ running: true, scanned: 0, truncated: false });
      // 찾는 족족 리스트에 반영 (백엔드가 200ms 단위로 묶어 보내므로 렌더가 몰아치지 않는다)
      searchPathRecursive(
        currentPath,
        query,
        (matches, scanned) => {
          if (matches.length) setDeepMatches((prev) => [...prev, ...matches]);
          setDeepSearch((prev) => (prev.scanned === scanned ? prev : { ...prev, scanned }));
        },
        controller.signal,
      )
        .then(({ scanned, truncated }) => {
          setDeepSearch({ running: false, scanned, truncated });
        })
        .catch(() => {
          if (!controller.signal.aborted) setDeepSearch({ running: false, scanned: 0, truncated: false });
        });
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [searchQuery, currentPath]);

  // 컨트롤 키
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Control") {
        setCtrlPressed(true);
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === "Control") {
        setCtrlPressed(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  // 현재 경로에 따른 서버의 폴더 및 파일
  useEffect(() => {
    const fetchData = async () => {
      try {
        setSelectedRows({ dirs: [], files: [] });
        const response = await getPathData(currentPath);
        if (response.contents.dir) {
          setDirs(response.contents.dir);
        } else {
          setDirs([]);
        }
        if (response.contents.file) {
          setFiles(response.contents.file);
        } else {
          setFiles([]);
        }
        lastValidPathRef.current = currentPath;
      } catch (error: any) {
        console.error(error);
        toast.error(error.message);
        // 진입 실패 시 이전 경로로 복귀 (같은 경로면 무한 루프 방지를 위해 그대로 둠)
        if (lastValidPathRef.current !== currentPath) {
          setCurrentPath(lastValidPathRef.current);
        }
      }
    };
    fetchData();
    setSearchQuery("");
  }, [currentPath]);

  useEffect(() => {
    const steps = TUTORIALS[tutorialKey];

    const selectImageFileIndex = steps.findIndex((s) => s.target === "#select-image-file");

    if (!run || stepIndex !== selectImageFileIndex) return;

    // 폴더를 두 단계 진입한 뒤 선택했을 때 다음 스텝(#open-label-modal)으로.
    const depth = (path: string) => path.split("/").filter(Boolean).length;
    if (depth(currentPath) - depth(tutorialBasePathRef.current) < 2) return;

    // '데이터셋 추가' 버튼은 선택이 있어야 렌더되므로, 선택 없이 넘어가면 다음 스텝 타겟이 사라진다.
    if (selectedRows.files.length === 0 && selectedRows.dirs.length === 0) return;

    // 폴더 더블클릭은 첫 클릭이 선택을 만들었다가 두번째 클릭이 비운다.
    const timer = setTimeout(() => nextSubStep(), 350);
    return () => clearTimeout(timer);
  }, [currentPath, selectedRows, run, stepIndex]);

  // NG 데이터가 존재한다면(빈배열) tempFile에 추가
  useEffect(() => {
    if (ngData && ngData.length > 0) {
      const ngDataItem = ngData[0] as unknown as NGDataItem;

      const fileObjects = ngDataItem.projectImageLabel.map((item, index) => ({
        id: index,
        label: "NG",
        path: item.projectImage.path,
      }));

      setTempFiles((prevFiles) => [...prevFiles, ...fileObjects]);
    }
  }, [ngData]);

  useEffect(() => {
    return () => {
      if (confirmedRef.current) return;
      setTempFiles(snapshotRef.current.files);
      setSelectedFile(snapshotRef.current.projects);
    };
  }, []);

  // includes(O(n))로 조회하면 렌더 1회가 O(n^2)라 창이 멈춤
  const selectedServerSet = useMemo(() => new Set(selectedServerRows), [selectedServerRows]);

  // 데이터셋 생성될 최종 선택된 파일
  const handleRegisterDataset = () => {
    const fileNamesForServer = tempFiles.map((file) => file.path);

    // 중복되는 파일 제거
    const uniqueFileNames = Array.from(new Set(fileNamesForServer));
    if (onClick) {
      onClick();
    }
    setSelectedRows({ dirs: [], files: [] });
    setSelectedServerRows([]);

    setServerFilesNames(uniqueFileNames);

    // 선택된 데이터셋 저장 (이전 데이터 포함, 중복 제거)
    setSelectedFile((prevSelectedFiles) => {
      const existingIds = new Set(prevSelectedFiles.map((file) => file.id));
      const newFiles = selectedDataSet.filter((file) => !existingIds.has(file.id));

      return [...prevSelectedFiles, ...newFiles];
    });
    console.log("tempFiles", tempFiles);
    setServerFiles(tempFiles);
    confirmedRef.current = true;
  };

  // 뒤로가기
  const handleBackToPath = () => {
    // 이미 최상단이면 막기
    if (currentPath === "/data1/datasets/") {
      toast.warning("마지막 경로입니다.");
      return;
    }

    // trailing slash 제거 후 처리
    const trimmed = currentPath.endsWith("/") ? currentPath.slice(0, -1) : currentPath;

    const segments = trimmed.split("/").filter(Boolean);

    // ROOT까지 돌아가야 하는 경우
    if (segments.length <= 2) {
      setCurrentPath("/data1/datasets/");
      return;
    }

    const newPath = "/" + segments.slice(0, -1).join("/") + "/";
    setCurrentPath(newPath);
  };

  const handleRowDoubleClick = async (params: { row: { type: string; name: string } }) => {
    const { type, name } = params.row;

    if (type === "dir") {
      setSelectedRows({ dirs: [], files: [] });
      const basePath = currentPath.endsWith("/") ? currentPath : currentPath + "/";
      const newPath = basePath + name + "/";
      setCurrentPath(newPath);
    }
  };
  // 서버 파일 클릭
  const handleRowClick = (params: { row: { id: string; type: string; name: string } }, event?: React.MouseEvent) => {
    const { type, name } = params.row;
    setSelectAllMatches(false);

    // shift: 기준 행 ~ 현재 행 범위 일괄 선택 (ctrl 함께 누르면 기존 선택에 추가)
    // ponytail: 기준은 인덱스가 아닌 행 id → 정렬/경로가 바뀌어도 엉뚱한 범위가 잡히지 않음
    const anchorIndex = sortedRows.findIndex((row) => row.id === lastRowClickIdRef.current);
    const currentIndex = sortedRows.findIndex((row) => row.id === params.row.id);
    if (event?.shiftKey && anchorIndex >= 0 && currentIndex >= 0) {
      const range = sortedRows
        .slice(Math.min(anchorIndex, currentIndex), Math.max(anchorIndex, currentIndex) + 1)
        .map((row) => ({ type: row.type, name: row.name }));
      const merge = (base: { type: string; name: string }[], added: { type: string; name: string }[]) => {
        const names = new Set(base.map((item) => item.name));
        return [...base, ...added.filter((item) => !names.has(item.name))];
      };
      const rangeDirs = range.filter((row) => row.type === "dir");
      const rangeFiles = range.filter((row) => row.type === "file");
      setSelectedRows((prev) =>
        ctrlPressed
          ? { dirs: merge(prev.dirs, rangeDirs), files: merge(prev.files, rangeFiles) }
          : { dirs: rangeDirs, files: rangeFiles },
      );
      return; // 기준점 유지
    }

    lastRowClickIdRef.current = params.row.id;
    setSelectedRows((prevSelected) => {
      let newSelected = { ...prevSelected };
      if (ctrlPressed) {
        if (type === "dir") {
          if (newSelected.dirs.some((dir) => dir.name === name)) {
            newSelected.dirs = newSelected.dirs.filter((dir) => dir.name !== name);
          } else {
            newSelected.dirs = [...newSelected.dirs, params.row];
          }
        } else if (type === "file") {
          if (newSelected.files.some((file) => file.name === name)) {
            newSelected.files = newSelected.files.filter((file) => file.name !== name);
          } else {
            newSelected.files = [...newSelected.files, params.row];
          }
        }
      } else {
        // Control 키가 눌리지 않았으면 선택 초기화하고 새로 선택
        if (type === "dir") {
          newSelected = { dirs: [params.row], files: [] };
        } else if (type === "file") {
          newSelected = { dirs: [], files: [params.row] };
        }
      }
      return newSelected;
    });
  };

  const handleAllSelect = () => {
    const sourceRows = filteredRows.length > 0 ? filteredRows : rows;

    const allDirs = sourceRows.filter((row) => row.type === "dir").map((row) => ({ type: row.type, name: row.name }));
    const allFiles = sourceRows.filter((row) => row.type === "file").map((row) => ({ type: row.type, name: row.name }));

    const isAllSelected = allDirs.length === selectedRows.dirs.length && allFiles.length === selectedRows.files.length;

    if (isAllSelected) {
      setSelectAllMatches(false);
      setSelectedRows({ dirs: [], files: [] });
    } else {
      // 검색 중이면 화면에 잘려 나온 500건이 아니라 검색 결과 전체가 대상 (펼치기는 백엔드가 진행)
      setSelectAllMatches(Boolean(searchQuery.trim()));
      setSelectedRows({ dirs: allDirs, files: allFiles });
    }
  };

  // 프로젝트 파일 클릭
  const handleProjectClick = async (params: any) => {
    setSelectedRowId(() => {
      if (selectedRowId.includes(params.row.id)) {
        return selectedRowId.filter((id) => id !== params.row.id);
      }
      return [...selectedRowId, params.row.id];
    });
    try {
      const response = await getProject(params.row.id);
      const allImageData = response.project.images;
      setSelectedDataSet((prevFiles) => {
        const selectedData = {
          id: params.row.id,
          project_name: params.row.project_name,
          icon: <ImageIcon />,
        };

        // 중복되는 id는 한번만 추가하고 무시
        const existingIds = new Set(prevFiles.map((file) => file.id));
        if (existingIds.has(selectedData.id)) {
          return prevFiles;
        }
        return [...prevFiles, selectedData];
      });

      setSelectedFile((prevFiles) => {
        const selectedData = {
          id: params.row.id,
          project_name: params.row.project_name,
        };
        const existingIds = new Set(prevFiles.map((file) => file.id));
        if (existingIds.has(selectedData.id)) {
          return prevFiles;
        }
        return [...prevFiles, selectedData];
      });

      setTempFiles((prevFiles) => {
        const allFileObjects = allImageData.map((file: IImageInfo) => ({
          id: file.id,
          name: file.path,
          icon: <ImageIcon />,
        }));
        // 중복되는 id는 한번만 추가하고 무시
        const existingIds = new Set(prevFiles.map((file) => file.id));
        const uniqueFileObjects = allFileObjects.filter((file: any) => !existingIds.has(file.id));
        return [...prevFiles, ...uniqueFileObjects];
      });
      // 전체 파일 목록 업데이트
      setTotalFiles((prevFiles) => {
        const allFileObjects = allImageData.map((file: IImageInfo) => ({
          id: file.id,
          name: file.path,
          icon: <ImageIcon />,
        }));
        return [...prevFiles, ...allFileObjects];
      });
      loadMoreData();
    } catch (error) {
      console.error(error);
    }
  };

  // temp 파일 클릭 (선택된 파일들 - 오른쪽)
  const handleTempFilesRowClick = useCallback(
    (event: React.MouseEvent, params: any, index: number) => {
      const rowPath = params.row.path as string;
      setSelectedRows({ dirs: [], files: [] });
      const anchor = lastTempClickIndexRef.current;
      if (event.shiftKey && anchor !== null) {
        // shift: 마지막 클릭 ~ 현재 클릭 범위 선택 (ctrl 함께 누르면 기존 선택에 추가)
        const rangePaths = tempFiles
          .slice(Math.min(anchor, index), Math.max(anchor, index) + 1)
          .map((file) => file.path);
        setSelectedServerRows((prev) => (ctrlPressed ? Array.from(new Set([...prev, ...rangePaths])) : rangePaths));
        return; // 기준점 유지
      }
      lastTempClickIndexRef.current = index;
      if (ctrlPressed) {
        setSelectedServerRows((prevSelectedServerFiles) => {
          if (selectedServerSet.has(rowPath)) {
            return prevSelectedServerFiles.filter((path) => path !== rowPath);
          } else {
            return [...prevSelectedServerFiles, rowPath];
          }
        });
      } else {
        setSelectedServerRows([rowPath]);
      }
    },
    [ctrlPressed, tempFiles, selectedServerSet],
  );

  // 데이터셋 추가 (라벨 선택 버튼)
  const handleSelectLabel = async () => {
    setIsOpenSelectLabel(true);
  };

  const handleAddDataset = async () => {
    // 선택된 폴더 및 파일 이름 추출
    const { dirs, files } = selectedRows;
    const basePath = currentPath.endsWith("/") ? currentPath : currentPath + "/";
    const dirNames = dirs.map((dir) => basePath + dir.name);
    const fileNames = files.map((file) => basePath + file.name);

    const controller = new AbortController();
    addAbortRef.current = controller;
    setIsLoadingFiles(true);
    let canceled = false;

    try {
      // 검색 결과 전체 선택이면 경로 목록 대신 검색 조건을 넘겨 백엔드가 전부 펼치게 한다
      const {
        files: selectedFiles,
        failedDirs,
        truncated,
      } = await getAllFilesFromDirs(
        dirNames,
        fileNames,
        selectAllMatches ? { path: currentPath, keyword: searchQuery.trim() } : undefined,
        controller.signal,
      );
      //  setTempFiles로 상태 갱신
      setTempFiles((prevFiles) => {
        // 기존 파일 복사 (얕은 복사)
        const updatedFiles = prevFiles.map((f) => ({ ...f }));

        // 이미 존재하는 파일 중 라벨이 다르면 덮어쓸지 확인
        const selectedSet = new Set(selectedFiles);
        const indexesToUpdate: number[] = [];
        updatedFiles.forEach((f, i) => {
          if (f.label !== selectedLabel && selectedSet.has(f.path)) indexesToUpdate.push(i);
        });

        if (indexesToUpdate.length > 0) {
          const confirmUpdate = window.confirm(
            `${indexesToUpdate.length}개의 기존 파일 라벨을 "${selectedLabel}"로 덮어씌우시겠습니까?`,
          );
          if (confirmUpdate) {
            // 새로운 객체로 label 변경
            indexesToUpdate.forEach((i) => {
              updatedFiles[i] = { ...updatedFiles[i], label: selectedLabel };
            });
          }
        }
        if (truncated) {
          alert(
            `한 번에 추가할 수 있는 상한(${selectedFiles.length}개)까지만 가져왔습니다. 범위를 나눠서 추가해주세요.`,
          );
        }
        if (failedDirs.length > 0) {
          const failedNames = failedDirs.map((dir) => dir.split("/").pop());
          alert(
            `${selectedFiles.length}개 항목 추가 완료 \n ${failedDirs.length}개 폴더 탐색 실패 \n실패: ${failedNames.join(", ")} 폴더 `,
          );
        }
        // 새 파일 추가
        const existingPaths = new Set(updatedFiles.map((f) => f.path));
        const newFiles = selectedFiles
          .filter((path) => !existingPaths.has(path))
          .map((filePath, index) => ({
            id: updatedFiles.length + index,
            path: filePath,
            label: selectedLabel,
          }));

        return [...updatedFiles, ...newFiles];
      });

      setIsOpenSelectLabel(false);
      setIsLoadingFiles(false);
      setIsOpenSelectLabel(false);

      if (run) {
        const steps = TUTORIALS[tutorialKey];

        if (stepIndex === steps.findIndex((s) => s.target === "#select-label")) {
          setTimeout(() => nextSubStep(), 100);
        }
      }
    } catch (error: any) {
      setIsLoadingFiles(false);
      if (error?.code === "ERR_CANCELED") {
        canceled = true; // 중지한 경우엔 선택을 그대로 둬서 바로 다시 시도할 수 있게 한다
        setIsOpenSelectLabel(false);
        toast.info("파일 탐색을 중지했습니다.");
      } else {
        console.error(error);
        toast.error("파일 목록을 불러오는 중 오류가 발생했습니다.");
      }
    } finally {
      addAbortRef.current = null;
      if (!canceled) {
        setSelectedRows({ dirs: [], files: [] });
        setSelectAllMatches(false);
      }
    }
  };

  // 탐색 중지 (요청을 끊으면 백엔드 크롤링도 멈춘다)
  const cancelAddDataset = () => addAbortRef.current?.abort();

  // '-' 버튼
  const handleRemoveDataset = () => {
    setTempFiles((prevTempFiles) => prevTempFiles.filter((file) => !selectedServerSet.has(file.path)));
    setSelectedServerRows([]);
  };

  // (오른쪽) 전체 선택 / 해제 토글
  const isAllTempSelected = tempFiles.length > 0 && selectedServerRows.length === tempFiles.length;
  const handleTempAllSelect = () => {
    setSelectedRows({ dirs: [], files: [] });
    setSelectedServerRows(isAllTempSelected ? [] : tempFiles.map((file) => file.path));
  };

  // 선택된 프로젝트 삭제 - Chip
  const handleRemoveChip = async (removeData: IDataSetObject) => {
    setSelectedDataSet((prevTempFiles) => prevTempFiles.filter((dataset) => dataset.id !== removeData.id));
    setSelectedFile((prevTempFiles) => prevTempFiles.filter((dataset) => dataset.id !== removeData.id));
    const response = await getProject(removeData.id);
    const allImageData = imageFlat(response.project.images);

    // tempFiles.name 과 allImageDataNames 비교하여 중복되는 이미지 삭제
    const removePaths = new Set(allImageData.map((item: IImageInfo) => item.path));
    const result = tempFiles.filter((file) => !removePaths.has(file.path));
    setTempFiles(result);
  };

  const handleLabelChange = (e: SelectChangeEvent) => {
    const value = e.target.value;
    setSelectedLabel(value);

    // setSelectedServer((prevFiles) =>
    //   prevFiles.map((file) => ({
    //     ...file,
    //     label: value,
    //   }))
    // );
  };

  const [sortAsc, setSortAsc] = useState<boolean>(true);

  const rows = [
    ...(dirs
      ? dirs.map((dir, index) => ({
          id: `dir-${index}`,
          name: dir,
          type: "dir",
          icon: <FolderIcon />,
        }))
      : []),
    ...(files
      ? files.map((file, index) => ({
          id: `file-${index}`,
          name: file,
          type: "file",
          icon: <ImageIcon />,
        }))
      : []),
  ];

  // 검색 중에는 현재 경로의 목록 대신 재귀 탐색 결과를 보여준다 (name은 currentPath 기준 상대 경로)
  const filteredRows = searchQuery.trim()
    ? deepMatches.map((match) => ({
        id: `${match.type}-${match.name}`,
        name: match.name,
        type: match.type,
        icon: match.type === "dir" ? <FolderIcon /> : <ImageIcon />,
      }))
    : rows;

  const sortedRows = [...filteredRows].sort((a, b) => {
    if (a.type !== b.type) return a.type === "dir" ? -1 : 1;
    const cmp = a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
    return sortAsc ? cmp : -cmp;
  });

  // Box resize 관련 코드
  const [leftWidth, setLeftWidth] = useState<number>(400);
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [dragOffset, setDragOffset] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        // 현재 마우스 위치 - 세로선의 현재 위치 차이
        setDragOffset(event.clientX - (containerRect.left + leftWidth));
      }
      setIsResizing(true);
    },
    [leftWidth],
  );

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (isResizing && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        let newWidth = event.clientX - containerRect.left - dragOffset;

        newWidth = Math.max(newWidth, 100);
        newWidth = Math.min(newWidth, containerRect.width - 100);

        setLeftWidth(newWidth);
      }
    },
    [isResizing, dragOffset],
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const selectedIds = [...selectedRows.dirs, ...selectedRows.files];
  const hasSelection = selectedRows.dirs.length > 0 || selectedRows.files.length > 0;

  // 새 폴더 추가 관련 코드
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renamingDir, setRenamingDir] = useState<string | null>(null); // 이름 수정 중인 폴더
  const [renameValue, setRenameValue] = useState("");

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(e.currentTarget);
    setMenuOpen((prev) => !prev);
  };

  const handleNewFolder = () => {
    setMenuOpen(false);
    setSearchQuery("");
    setNewFolderName("New Folder");
    setIsCreatingFolder(true);
  };

  const cancelNewFolder = () => {
    setIsCreatingFolder(false);
    setNewFolderName("");
  };

  // 새 폴더 / 폴더명 수정 공통 검사. 통과하면 null
  const invalidFolderName = (name: string) => {
    if (/[\\/]/.test(name) || name === "." || name === "..") return "폴더 이름에 / 또는 \\ 를 사용할 수 없습니다.";
    if (dirs.includes(name)) return "이미 존재하는 폴더 이름입니다.";
    return null;
  };

  const startRenameDir = (name: string) => {
    setRenamingDir(name);
    setRenameValue(name);
  };

  const cancelRenameDir = () => {
    setRenamingDir(null);
    setRenameValue("");
  };

  const submitRenameDir = () => {
    const name = renameValue.trim();
    if (!name || name === renamingDir) return cancelRenameDir();
    const error = invalidFolderName(name);
    if (error) {
      toast.info(error);
      return;
    }
    const confirmed = window.confirm(
      "폴더명을 수정하면 해당 파일을 사용하고 있는 데이터셋 파일이 모두 깨질 수 있습니다. 수정하시겠습니까?",
    );
    if (!confirmed) return;
    // ponytail: UI만. 서버 rename API 생기면 여기서 호출하고 setDirs로 교체
    toast.info("폴더명 수정 기능은 아직 지원하지 않습니다.");
    cancelRenameDir();
  };

  const submitNewFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return cancelNewFolder();
    const error = invalidFolderName(name);
    if (error) {
      toast.info(error);
      return;
    }
    try {
      await createServerDir(currentPath, name);
      setDirs((prev) => [...prev, name]);
      cancelNewFolder();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  // except 목록 적용: 한 줄에 이미지 fullPath 하나씩 → 학습(train)에서 빼고 검증(valid)으로 보냄
  const applyExceptText = (text: string) => {
    const paths = text
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    if (paths.length === 0) {
      toast.warning("경로가 없습니다.");
      return false;
    }
    const unique = Array.from(new Set(paths));
    setExceptPaths(unique);

    const matched = unique.filter((path) => tempFiles.some((f) => f.path === path)).length;
    toast.success(`학습 제외 ${unique.length}개 설정 (현재 목록과 일치: ${matched}개)`);
    return true;
  };

  // except.txt 첨부
  const handleExceptFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    applyExceptText(await file.text());
  };

  // 파일 업로드 관련코드
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    try {
      const uploadedFiles: { name: string; id: string }[] = [];

      for (const file of Array.from(files)) {
        await uploadFile(currentPath, file);
        uploadedFiles.push({
          name: file.name,
          id: "",
        });
      }

      toast.success(`${files.length}개의 파일이 업로드되었습니다.`);

      // 파일 목록 새로고침을 위해 현재 경로 다시 불러오기
      setFiles((prevFiles) => [...prevFiles, ...uploadedFiles.map((file) => file.name)]);
    } catch (error) {
      console.error(error);
    }

    event.target.value = "";
  };

  return (
    <>
      <Box ref={containerRef} sx={{ ...modalContentStyle, ...contentSx }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: "18px",
            height: "52px",
            borderBottom: "1px solid var(--dk-border, #e8e8f0)",
            background: "var(--dk-surface-2, #fafafa)",
            flexShrink: 0,
          }}
        >
          {selectedTab === "project" ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: "#3b82f6" }} />
              <Typography
                sx={{ fontSize: "13px", fontWeight: 600, color: "var(--dk-text, #1a1a2e)", letterSpacing: "0.01em" }}
              >
                {t("Project")}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flex: 1 }}>
              <IconButton
                onClick={handleBackToPath}
                size="small"
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "7px",
                  border: "1px solid var(--dk-border, #e0e0ee)",
                  color: "#6060808",
                  "&:hover": { background: "#f0f0f8", color: "var(--dk-text, #1a1a2e)" },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 14 }} />
              </IconButton>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  px: "10px",
                  py: "4px",
                  borderRadius: "7px",
                  background: "var(--dk-surface-2, #f4f4f8)",
                  border: "1px solid var(--dk-border, #e4e4ef)",
                  flex: 1,
                  overflow: "hidden",
                }}
              >
                <FolderOpenIcon sx={{ fontSize: 13, color: "#3b82f6", flexShrink: 0 }} />
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#7070908",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {currentPath}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
          {/* 왼쪽 패널 */}
          <Box
            id="server-image-list"
            sx={{
              width: `${leftWidth}px`,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            {selectedTab === "project" ? (
              <Box sx={{ flex: 1, overflow: "hidden" }}>
                <ProjectFileModal onRowClick={handleProjectClick} selectedRowId={selectedRowId} />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    px: "12px",
                    height: "48px",
                    borderBottom: "1px solid #ededf5",
                    background: "var(--dk-surface, #fff)",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      flex: 1,
                      height: "32px",
                      px: "10px",
                      borderRadius: "8px",
                      border: "1px solid var(--dk-border, #e0e0ee)",
                      background: "#f8f8fc",
                      transition: "all .15s",
                      "&:focus-within": {
                        border: "1px solid #93c5fd",
                        background: "var(--dk-info-bg, #eff6ff)",
                      },
                    }}
                  >
                    <SearchIcon sx={{ fontSize: 14, color: "var(--dk-text-3, #b0b0c8)", flexShrink: 0 }} />
                    <input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="파일 / 폴더 검색..."
                      style={{
                        flex: 1,
                        background: "transparent",
                        border: "none",
                        outline: "none",
                        fontSize: "12px",
                        color: "var(--dk-text, #2a2a4a)",
                        fontFamily: "inherit",
                      }}
                    />
                    {searchQuery && (
                      <Box
                        component="span"
                        onClick={() => setSearchQuery("")}
                        sx={{
                          fontSize: 12,
                          color: "var(--dk-text-3, #b0b0c8)",
                          cursor: "pointer",
                          lineHeight: 1,
                          "&:hover": { color: "#6060808" },
                        }}
                      >
                        ✕
                      </Box>
                    )}
                  </Box>
                  {hasSelection || searchQuery.trim() ? (
                    <Box sx={{ display: "flex", gap: "4px", flexShrink: 0 }}>
                      <Tooltip title={t("SelectAll")}>
                        <IconButton
                          size="small"
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "7px",
                            border: "1px solid var(--dk-border, #e0e0ee)",
                            color: "#7070908",
                            "&:hover": { background: "var(--dk-surface-2, #f4f4f8)" },
                          }}
                          onClick={handleAllSelect}
                        >
                          <DoneAllIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("AddDataset")}>
                        <span>
                          <IconButton
                            id="open-label-modal"
                            size="small"
                            disabled={!hasSelection}
                            onClick={handleSelectLabel}
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: "7px",
                              border: "1px solid var(--dk-info-border, #bfdbfe)",
                              color: "#3b82f6",
                              "&:hover": { background: "var(--dk-info-bg, #eff6ff)" },
                              "&.Mui-disabled": { opacity: 0.4, color: "#3b82f6" },
                            }}
                          >
                            <DriveFileMoveIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t("Delete")}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={!hasSelection}
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: "7px",
                              border: "1px solid #fecaca",
                              color: "#ef4444",
                              "&:hover": { background: "#fff5f5" },
                              "&.Mui-disabled": { opacity: 0.4, color: "#ef4444" },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Box sx={{ flexShrink: 0 }}>
                      <IconButton
                        id="upload-button"
                        onClick={handleMenuOpen}
                        size="small"
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "7px",
                          border: "1px solid var(--dk-border, #e0e0ee)",
                          color: "var(--dk-text-3, #9090a8)",
                          "&:hover": { background: "var(--dk-surface-2, #f4f4f8)", color: "var(--dk-text, #2a2a4a)" },
                        }}
                      >
                        <AddToDriveIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                      <Menu
                        open={menuOpen}
                        onClose={() => setMenuOpen(false)}
                        anchorEl={menuAnchorEl}
                        disableRestoreFocus // 닫힐 때 버튼으로 포커스가 돌아가 입력칸 포커스를 뺏지 않도록
                        PaperProps={{
                          sx: {
                            background: "var(--dk-surface, #fff)",
                            border: "1px solid var(--dk-border, #e4e4ef)",
                            borderRadius: "10px",
                            boxShadow: "0 8px 24px rgba(80,70,180,0.10)",
                            mt: "4px",
                            "& .MuiMenuItem-root": {
                              fontSize: 13,
                              gap: "8px",
                              color: "var(--dk-text-2, #3a3a5c)",
                              borderRadius: "6px",
                              mx: "4px",
                              my: "2px",
                              "&:hover": { background: "var(--dk-surface-2, #f4f4f8)" },
                            },
                          },
                        }}
                      >
                        <MenuItem onClick={handleNewFolder}>
                          <FolderOpenIcon sx={{ fontSize: 15, color: "var(--dk-text-3, #9090a8)" }} />
                          {t("NewFolder")}
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setMenuOpen(false);
                            fileInputRef.current?.click();
                          }}
                        >
                          <UploadFileIcon sx={{ fontSize: 15, color: "var(--dk-text-3, #9090a8)" }} />
                          {t("UploadFile")}
                        </MenuItem>
                      </Menu>
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleFileSelect}
                        accept="image/*"
                        multiple
                      />
                    </Box>
                  )}
                </Box>
                {searchQuery && (
                  <Box
                    sx={{
                      px: "14px",
                      py: "6px",
                      borderBottom: "1px solid #f0f0f8",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      background: "var(--dk-surface-2, #fafafa)",
                    }}
                  >
                    <Typography sx={{ fontSize: 11, color: "var(--dk-text-3, #9090a8)" }}>검색결과</Typography>
                    <Box
                      sx={{
                        px: "6px",
                        py: "1px",
                        borderRadius: "4px",
                        background: "var(--dk-info-bg, #eff6ff)",
                        border: "1px solid var(--dk-info-border, #bfdbfe)",
                      }}
                    >
                      <Typography sx={{ fontSize: 11, color: "#3b82f6", fontWeight: 600 }}>
                        {filteredRows.length}건{deepSearch.truncated && " 이상"}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 11, color: "var(--dk-text-3, #b0b0c8)" }}>"{searchQuery}"</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "5px", ml: "auto" }}>
                      {deepSearch.running && <CircularProgress size={10} thickness={6} sx={{ color: "#3b82f6" }} />}
                      <Typography sx={{ fontSize: 11, color: "var(--dk-text-3, #9090a8)" }}>
                        {deepSearch.running
                          ? `하위 폴더 ${deepSearch.scanned}개 탐색 중...`
                          : `하위 폴더 ${deepSearch.scanned}개 탐색 완료`}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {/* ponytail: MIT DataGrid는 페이지당 100행 제한 → 오른쪽 패널과 같은 slice + 스크롤 방식 */}
                <Box
                  sx={{
                    px: "16px",
                    py: "10px",
                    borderBottom: "1px solid #ededf5",
                    background: "#f8f8fc",
                    fontWeight: 600,
                    fontSize: "11px",
                    color: "var(--dk-text-3, #9090a8)",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    flexShrink: 0,
                    cursor: "pointer",
                    userSelect: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "8px",
                  }}
                  onClick={() => setSortAsc((prev) => !prev)}
                >
                  <span>Name {sortAsc ? "▲" : "▼"}</span>
                  {(selectedRows.dirs.length > 0 || selectedRows.files.length > 0) && (
                    <span style={{ color: "#3b82f6", textTransform: "none", letterSpacing: "normal" }}>
                      {selectAllMatches
                        ? "검색 결과 전체 선택 (하위 폴더 포함)"
                        : `${[
                            selectedRows.dirs.length > 0 && `폴더 ${selectedRows.dirs.length}`,
                            selectedRows.files.length > 0 && `파일 ${selectedRows.files.length}`,
                          ]
                            .filter(Boolean)
                            .join(" · ")} 선택`}
                    </span>
                  )}
                </Box>
                <Box
                  id="select-image-file"
                  onScroll={handleScroll(() => setVisibleCountLeft((prev) => prev + 100))}
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    overflowX: "hidden",
                    background: "var(--dk-surface, #fff)",
                    "&::-webkit-scrollbar": { width: "4px" },
                    "&::-webkit-scrollbar-thumb": { backgroundColor: "#d0d0e0", borderRadius: "999px" },
                  }}
                >
                  {isCreatingFolder && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        height: "48px",
                        px: "10px",
                        borderBottom: "1px solid #f4f4f8",
                        background: "var(--dk-info-bg, #eff6ff)",
                        borderLeft: "2px solid #3b82f6",
                      }}
                    >
                      <FolderIcon />
                      <InputBase
                        autoFocus
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => {
                          e.stopPropagation(); // Esc가 바깥 Modal까지 닫지 않도록
                          if (e.key === "Enter" && !e.nativeEvent.isComposing) submitNewFolder();
                          if (e.key === "Escape") cancelNewFolder();
                        }}
                        inputProps={{ "aria-label": t("NewFolder") }}
                        sx={{
                          flex: 1,
                          fontSize: "13px",
                          px: "6px",
                          border: "1px solid #93c5fd",
                          borderRadius: "4px",
                          background: "var(--dk-surface, #fff)",
                        }}
                      />
                      <Button size="small" onClick={submitNewFolder} sx={{ minWidth: 0, fontSize: 12 }}>
                        {t("Confirm")}
                      </Button>
                      <Button size="small" color="inherit" onClick={cancelNewFolder} sx={{ minWidth: 0, fontSize: 12 }}>
                        {t("Cancel")}
                      </Button>
                    </Box>
                  )}
                  {rows.length === 0 && !isCreatingFolder && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        height: "100%",
                        minHeight: "160px",
                        color: "var(--dk-text-3, #b0b0c8)",
                      }}
                    >
                      <FolderOpenIcon sx={{ fontSize: 36 }} />
                      <Typography sx={{ fontSize: 13, color: "var(--dk-text-3, #9090a8)" }}>
                        {t("EmptyFolder")}
                      </Typography>
                    </Box>
                  )}
                  {sortedRows.slice(0, visibleCountLeft).map((row) => {
                    const isSelected =
                      selectedRows.dirs.some((d) => d.name === row.name) ||
                      selectedRows.files.some((f) => f.name === row.name);
                    return (
                      <Box
                        key={row.id}
                        onClick={(event) => handleRowClick({ row }, event)}
                        onDoubleClick={() => handleRowDoubleClick({ row })}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          height: "48px",
                          px: "10px",
                          cursor: "pointer",
                          userSelect: "none",
                          fontSize: "13px",
                          color: "var(--dk-text, #2a2a4a)",
                          borderBottom: "1px solid #f4f4f8",
                          transition: "background .1s",
                          background: isSelected ? "#eff6ff" : "transparent",
                          borderLeft: isSelected ? "2px solid #3b82f6" : "2px solid transparent",
                          "&:hover": {
                            background: isSelected ? "#dbeafe" : "#f8f8ff",
                            "& .rename-dir-button": { opacity: 1 },
                          },
                        }}
                      >
                        {row.icon}
                        {renamingDir === row.name && row.type === "dir" ? (
                          <Box
                            sx={{ display: "flex", alignItems: "center", gap: "6px", flex: 1 }}
                            onClick={(e) => e.stopPropagation()}
                            onDoubleClick={(e) => e.stopPropagation()}
                          >
                            <InputBase
                              autoFocus
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              onFocus={(e) => e.target.select()}
                              onKeyDown={(e) => {
                                e.stopPropagation();
                                if (e.key === "Enter" && !e.nativeEvent.isComposing) submitRenameDir();
                                if (e.key === "Escape") cancelRenameDir();
                              }}
                              inputProps={{ "aria-label": t("Rename") }}
                              sx={{
                                flex: 1,
                                fontSize: "13px",
                                px: "6px",
                                border: "1px solid #93c5fd",
                                borderRadius: "4px",
                                background: "var(--dk-surface, #fff)",
                              }}
                            />
                            <Button size="small" onClick={submitRenameDir} sx={{ minWidth: 0, fontSize: 12 }}>
                              {t("Confirm")}
                            </Button>
                            <Button
                              size="small"
                              color="inherit"
                              onClick={cancelRenameDir}
                              sx={{ minWidth: 0, fontSize: 12 }}
                            >
                              {t("Cancel")}
                            </Button>
                          </Box>
                        ) : (
                          <>
                            <span
                              style={{
                                flex: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.name}
                            </span>
                            {row.type === "dir" && !searchQuery.trim() && (
                              <Tooltip title={t("Rename")}>
                                <IconButton
                                  size="small"
                                  className="rename-dir-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    startRenameDir(row.name);
                                  }}
                                  onDoubleClick={(e) => e.stopPropagation()}
                                  sx={{ width: 24, height: 24, opacity: 0, color: "#707090" }}
                                >
                                  <DriveFileRenameOutlineIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}
          </Box>
          <Box
            sx={{
              width: "4px",
              background: "#e8e8f0",
              cursor: "ew-resize",
              flexShrink: 0,
              transition: "all .15s",
              "&:hover": { background: "#93c5fd", width: "4px" },
            }}
            onMouseDown={handleMouseDown}
          />
          {/* 오른쪽 패널 */}
          <Box
            id="temp-image-list"
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              minWidth: 0,
              background: "var(--dk-surface, #fff)",
            }}
          >
            <Box
              sx={{
                height: "48px",
                px: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #ededf5",
                background: "var(--dk-surface-2, #fafafa)",
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "11px",
                  color: "var(--dk-text-3, #9090a8)",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                Selected Dataset
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    px: "8px",
                    py: "3px",
                    borderRadius: "6px",
                    background: "var(--dk-info-bg, #eff6ff)",
                    border: "1px solid var(--dk-info-border, #bfdbfe)",
                  }}
                >
                  <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#3b82f6" }}>
                    {tempFiles.length}
                  </Typography>
                  <Typography sx={{ fontSize: "11px", color: "#93c5fd" }}>files</Typography>
                </Box>
                {step === "검사" && (
                  <>
                    {exceptPaths.length > 0 && (
                      <Chip
                        size="small"
                        label={`학습 제외 ${exceptPaths.length}`}
                        onDelete={() => setExceptPaths([])}
                        sx={{
                          height: "22px",
                          fontSize: "11px",
                          fontWeight: 600,
                          background: "#fff7ed",
                          border: "1px solid #fed7aa",
                          color: "#ea580c",
                          "& .MuiChip-deleteIcon": { color: "#fb923c" },
                        }}
                      />
                    )}
                    <Tooltip title="경로 직접 붙여넣기 (파일 반출이 막힌 PC용)">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setExceptText(exceptPaths.join("\n"));
                          setIsOpenExceptPaste(true);
                        }}
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "7px",
                          border: "1px solid #fed7aa",
                          color: "#ea580c",
                          "&:hover": { background: "#fff7ed" },
                        }}
                      >
                        <DescriptionIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="except.txt 첨부 (파일에 적힌 이미지는 학습에서 제외되고 검증(valid)으로 들어갑니다)">
                      <IconButton
                        size="small"
                        onClick={() => exceptInputRef.current?.click()}
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "7px",
                          border: "1px solid #fed7aa",
                          color: "#ea580c",
                          "&:hover": { background: "#fff7ed" },
                        }}
                      >
                        <UploadFileIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <input
                      type="file"
                      ref={exceptInputRef}
                      style={{ display: "none" }}
                      onChange={handleExceptFileSelect}
                      accept=".txt,text/plain"
                    />
                  </>
                )}
              </Box>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "90px 1fr 60px",
                alignItems: "center",
                px: "16px",
                py: "8px",
                borderBottom: "1px solid #f0f0f8",
                background: "#f8f8fc",
              }}
            >
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "var(--dk-text-3, #9090a8)",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                라벨
              </Typography>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "var(--dk-text-3, #9090a8)",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Image Name
              </Typography>
              <Box sx={{ display: "flex", gap: "4px", justifySelf: "end" }}>
                <Tooltip title={isAllTempSelected ? t("DeselectAll") : t("SelectAll")}>
                  <span>
                    <IconButton
                      id="select-all-temp-image-list"
                      size="small"
                      disabled={tempFiles.length === 0}
                      onClick={handleTempAllSelect}
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "6px",
                        color: isAllTempSelected ? "#3b82f6" : "#707090",
                        "&:hover": { background: "var(--dk-surface-2, #f4f4f8)" },
                      }}
                    >
                      <DoneAllIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="선택 항목 제거">
                  <IconButton
                    id="remove-temp-image-list"
                    size="small"
                    onClick={handleRemoveDataset}
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "6px",
                      color: "#f87171",
                      "&:hover": { background: "#fff5f5" },
                    }}
                  >
                    <RemoveIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-thumb": { backgroundColor: "#d0d0e0", borderRadius: "999px" },
              }}
              onScroll={handleScroll(loadMoreData)}
            >
              {tempFiles.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    gap: "10px",
                    opacity: 0.4,
                  }}
                >
                  <DriveFileMoveIcon sx={{ fontSize: 32, color: "var(--dk-text-3, #b0b0c8)" }} />
                  <Typography sx={{ fontSize: "12px", color: "var(--dk-text-3, #9090a8)" }}>
                    파일을 선택하면 여기에 표시됩니다
                  </Typography>
                </Box>
              ) : (
                tempFiles.slice(0, visibleCountRight).map((item, index) => (
                  <Box
                    key={index}
                    onClick={(e) => handleTempFilesRowClick(e, { row: item }, index)}
                    sx={{
                      userSelect: "none",
                      display: "grid",
                      gridTemplateColumns: "90px 1fr",
                      gap: "12px",
                      alignItems: "center",
                      px: "16px",
                      py: "10px",
                      cursor: "pointer",
                      transition: "background .1s",
                      borderBottom: "1px solid #f4f4f8",
                      background: selectedServerSet.has(item.path) ? "#eff6ff" : "transparent",
                      borderLeft: selectedServerSet.has(item.path) ? "2px solid #3b82f6" : "2px solid transparent",
                      "&:hover": { background: "#f8f8ff" },
                    }}
                  >
                    <Chip
                      label={item.label}
                      size="small"
                      sx={{
                        height: "22px",
                        fontSize: "11px",
                        fontWeight: 600,
                        maxWidth: "82px",
                        ...labelChipSx(item.label),
                        "& .MuiChip-label": { px: "8px" },
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#4a4a6a",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                        lineHeight: 1.5,
                      }}
                    >
                      {step === "검사" && exceptSet.has(item.path) && (
                        <Box
                          component="span"
                          sx={{
                            mr: "6px",
                            px: "5px",
                            py: "1px",
                            borderRadius: "4px",
                            fontSize: "10px",
                            fontWeight: 700,
                            background: "#fff7ed",
                            border: "1px solid #fed7aa",
                            color: "#ea580c",
                          }}
                        >
                          학습 제외
                        </Box>
                      )}
                      {item.path}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            ...bottomBoxStyle,
            borderTop: "1px solid #ededf5",
            background: "var(--dk-surface-2, #fafafa)",
            px: "16px",
            py: "10px",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%", gap: "8px" }}>
            {selectedFile.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  gap: "5px",
                  overflowX: "auto",
                  whiteSpace: "nowrap",
                  pb: "2px",
                  "&::-webkit-scrollbar": { height: "3px" },
                  "&::-webkit-scrollbar-thumb": { backgroundColor: "#d0d0e0", borderRadius: "999px" },
                }}
              >
                {selectedFile.map((file) => (
                  <RenderChips
                    id={file.id}
                    project_name={file.project_name}
                    handleDelete={() => handleRemoveChip(file)}
                  />
                ))}
              </Box>
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "6px" }}>
              <Button
                onClick={onClick}
                sx={{
                  fontSize: "12px",
                  px: "14px",
                  py: "5px",
                  borderRadius: "8px",
                  border: "1px solid var(--dk-border, #e0e0ee)",
                  color: "#7070908",
                  textTransform: "none",
                  "&:hover": { background: "var(--dk-surface-2, #f4f4f8)" },
                }}
              >
                {t("Cancel")}
              </Button>
              <Button
                id="confirm-create-dataset"
                onClick={() => {
                  confirmedRef.current = true; // OK로 닫힌 경우 → 되돌리지 않음
                  (onConfirm ?? handleRegisterDataset)();
                }}
                sx={{
                  fontSize: "12px",
                  px: "16px",
                  py: "5px",
                  borderRadius: "8px",
                  background: "var(--dk-info-bg, #eff6ff)",
                  border: "1px solid var(--dk-info-border, #bfdbfe)",
                  color: "#3b82f6",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": { background: "#dbeafe", borderColor: "#3b82f6" },
                }}
              >
                {t("OK")}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      {isOpenExceptPaste && (
        <Modal open onClose={() => setIsOpenExceptPaste(false)} sx={{ zIndex: 1300 }}>
          <Box
            sx={{
              width: "560px",
              background: "var(--dk-surface, #fff)",
              border: "1px solid var(--dk-border, #e4e4ef)",
              borderRadius: "14px",
              boxShadow: "0 16px 48px rgba(80,70,180,0.12)",
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: "20px",
                py: "14px",
                borderBottom: "1px solid #ededf5",
                background: "var(--dk-surface-2, #fafafa)",
              }}
            >
              <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "var(--dk-text, #1a1a2e)" }}>
                학습 제외 경로 붙여넣기
              </Typography>
              <IconButton
                onClick={() => setIsOpenExceptPaste(false)}
                size="small"
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: "6px",
                  color: "var(--dk-text-3, #9090a8)",
                  "&:hover": { background: "var(--dk-surface-2, #f4f4f8)" },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
            <Box sx={{ px: "20px", py: "16px", display: "flex", flexDirection: "column", gap: "12px" }}>
              <Typography sx={{ fontSize: "12px", color: "var(--dk-text-3, #9090a8)", lineHeight: 1.6 }}>
                except.txt 내용을 그대로 붙여넣으세요. 한 줄에 이미지 경로 하나씩, 적힌 이미지는 학습에서 제외되고
                검증(valid)으로 들어갑니다.
              </Typography>
              <TextField
                autoFocus
                multiline
                minRows={10}
                maxRows={14}
                fullWidth
                value={exceptText}
                onChange={(e) => setExceptText(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder={"/data1/datasets/.../1-1-21.JPG\n/data1/datasets/..."}
                sx={{
                  "& .MuiInputBase-root": {
                    fontSize: "12px",
                    fontFamily: "monospace",
                    borderRadius: "8px",
                    background: "#f8f8fc",
                  },
                }}
              />
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ fontSize: "11px", color: "var(--dk-text-3, #b0b0c8)" }}>
                  {exceptText.split("\n").filter((line) => line.trim()).length}줄
                </Typography>
                <Box sx={{ display: "flex", gap: "6px" }}>
                  <Button
                    onClick={() => setIsOpenExceptPaste(false)}
                    sx={{
                      fontSize: "12px",
                      px: "14px",
                      py: "5px",
                      borderRadius: "8px",
                      border: "1px solid var(--dk-border, #e0e0ee)",
                      color: "var(--dk-text-3, #9090a8)",
                      textTransform: "none",
                      "&:hover": { background: "var(--dk-surface-2, #f4f4f8)" },
                    }}
                  >
                    {t("Cancel")}
                  </Button>
                  <Button
                    onClick={() => {
                      if (applyExceptText(exceptText)) setIsOpenExceptPaste(false);
                    }}
                    sx={{
                      fontSize: "12px",
                      px: "16px",
                      py: "5px",
                      borderRadius: "8px",
                      background: "#fff7ed",
                      border: "1px solid #fed7aa",
                      color: "#ea580c",
                      fontWeight: 600,
                      textTransform: "none",
                      "&:hover": { background: "#ffedd5", borderColor: "#fb923c" },
                    }}
                  >
                    적용
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Modal>
      )}
      {isOpenSelectLabel && (
        <Modal
          id="select-label"
          open={isOpenSelectLabel}
          onClose={() => {
            if (isLoadingFiles) {
              cancelAddDataset();
              return;
            }
            setIsOpenSelectLabel(false);
            setSelectedLabel("unlabeled");
          }}
          sx={{ zIndex: 1300 }}
        >
          <Box
            sx={{
              width: "360px",
              background: "var(--dk-surface, #fff)",
              border: "1px solid var(--dk-border, #e4e4ef)",
              borderRadius: "14px",
              boxShadow: "0 16px 48px rgba(80,70,180,0.12)",
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              overflow: "hidden",
            }}
          >
            {!isLoadingFiles && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: "20px",
                  py: "14px",
                  borderBottom: "1px solid #ededf5",
                  background: "var(--dk-surface-2, #fafafa)",
                }}
              >
                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "var(--dk-text, #1a1a2e)" }}>
                  이동할 위치 선택
                </Typography>
                <IconButton
                  onClick={() => {
                    setIsOpenSelectLabel(false);
                    setSelectedLabel("unlabeled");
                  }}
                  size="small"
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "6px",
                    color: "var(--dk-text-3, #9090a8)",
                    "&:hover": { background: "var(--dk-surface-2, #f4f4f8)" },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            )}
            <Box sx={{ px: "20px", py: "16px", display: "flex", flexDirection: "column", gap: "14px" }}>
              {!isLoadingFiles ? (
                <>
                  <Typography sx={{ fontSize: "12px", color: "var(--dk-text-3, #9090a8)", lineHeight: 1.6 }}>
                    선택한 파일들을 이동할 라벨을 선택해주세요
                  </Typography>
                  <Select
                    value={selectedLabel}
                    onChange={handleLabelChange}
                    fullWidth
                    size="small"
                    sx={{
                      borderRadius: "8px",
                      background: "#f8f8fc",
                      fontSize: "13px",
                      color: "var(--dk-text, #2a2a4a)",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--dk-border, #e0e0ee)" },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#b0b0d0" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#3b82f6" },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          background: "var(--dk-surface, #fff)",
                          border: "1px solid var(--dk-border, #e4e4ef)",
                          borderRadius: "10px",
                          boxShadow: "0 8px 24px rgba(80,70,180,0.10)",
                          "& .MuiMenuItem-root": {
                            fontSize: 13,
                            color: "var(--dk-text-2, #3a3a5c)",
                            borderRadius: "6px",
                            mx: "4px",
                            my: "2px",
                            "&:hover": { background: "var(--dk-surface-2, #f4f4f8)" },
                            "&.Mui-selected": { background: "var(--dk-info-bg, #eff6ff)", color: "#3b82f6" },
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="unlabeled">unlabeled</MenuItem>
                    <MenuItem value="OK">OK</MenuItem>
                    <MenuItem value="NG">NG</MenuItem>
                  </Select>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "6px" }}>
                    <Button
                      onClick={() => {
                        setIsOpenSelectLabel(false);
                        setSelectedLabel("unlabeled");
                      }}
                      sx={{
                        fontSize: "12px",
                        px: "14px",
                        py: "5px",
                        borderRadius: "8px",
                        border: "1px solid var(--dk-border, #e0e0ee)",
                        color: "var(--dk-text-3, #9090a8)",
                        textTransform: "none",
                        "&:hover": { background: "var(--dk-surface-2, #f4f4f8)" },
                      }}
                    >
                      {t("Cancel")}
                    </Button>
                    <Button
                      id="confirm-file-btn"
                      onClick={handleAddDataset}
                      sx={{
                        fontSize: "12px",
                        px: "16px",
                        py: "5px",
                        borderRadius: "8px",
                        background: "var(--dk-info-bg, #eff6ff)",
                        border: "1px solid var(--dk-info-border, #bfdbfe)",
                        color: "#3b82f6",
                        fontWeight: 600,
                        textTransform: "none",
                        "&:hover": { background: "#dbeafe", borderColor: "#3b82f6" },
                      }}
                    >
                      {t("Move")}
                    </Button>
                  </Box>
                </>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "20px", py: "8px" }}>
                  <Tooltip title="탐색 중지">
                    <IconButton
                      onClick={cancelAddDataset}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: "8px",
                        right: "8px",
                        width: 26,
                        height: 26,
                        borderRadius: "6px",
                        color: "var(--dk-text-3, #9090a8)",
                        "&:hover": { background: "var(--dk-surface-2, #f4f4f8)" },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CircularProgress size={52} thickness={3} sx={{ color: "#3b82f6" }} />
                    <FolderOpenIcon
                      sx={{
                        position: "absolute",
                        fontSize: 20,
                        color: "#3b82f6",
                      }}
                    />
                  </Box>
                  <Box sx={{ textAlign: "center", display: "flex", flexDirection: "column", gap: "4px" }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "var(--dk-text, #1a1a2e)" }}>
                      파일을 불러오는 중...
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "var(--dk-text-3, #9090a8)" }}>
                      서버에서 하위 폴더까지 훑는 중
                    </Typography>
                  </Box>
                  <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "6px" }}>
                    <Box
                      sx={{
                        width: "100%",
                        height: "4px",
                        borderRadius: "999px",
                        background: "#f0f0f8",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          borderRadius: "999px",
                          background: "linear-gradient(90deg, #3b82f6, #93c5fd)",
                          animation: "indeterminate 1.5s ease-in-out infinite",
                          "@keyframes indeterminate": {
                            "0%": { width: "0%", marginLeft: "0%" },
                            "50%": { width: "60%", marginLeft: "20%" },
                            "100%": { width: "0%", marginLeft: "100%" },
                          },
                        }}
                      />
                    </Box>
                  </Box>

                  <Typography sx={{ fontSize: "11px", color: "var(--dk-text-3, #b0b0c8)", textAlign: "center" }}>
                    잠시 기다려주세요 · 중지하려면 X를 누르세요
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Modal>
      )}
    </>
  );
}
